import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
  FILTER_FIELD_STYLE,
  FormProvider,
  RHFControl,
  RHFDateRangePicker,
  RHFSelect
} from 'components/common/form';
import {
  useSponsorshipPriorities,
  useSponsorshipStatuses,
  useSponsorshipTypes
} from 'hooks/useBeneficiaries';

// Every filter is a multi-select: values within one filter are ORed, filters
// are ANDed together (same behaviour as the Pledges filter bar).
const AGE_OPTIONS = [
  { label: 'Under 5', value: '0-4' },
  { label: '5 – 10', value: '5-10' },
  { label: '11 – 15', value: '11-15' },
  { label: '16 – 17', value: '16-17' },
  { label: '18 and over', value: '18-200' }
];

// Date filters are one range field each: start and end, or just a start
const EMPTY_RANGE = { from: '', to: '' };
// Wide enough for "dd/mm/yyyy - dd/mm/yyyy" plus the clear button
const RANGE_COL_STYLE = { ...FILTER_FIELD_STYLE, minWidth: '15rem' };

const DEFAULT_VALUES = {
  country: [],
  gender: [],
  priority: [],
  type: [],
  status: [],
  juz: [],
  name: '',
  age: [],
  facility: [],
  enrolled: EMPTY_RANGE,
  left: EMPTY_RANGE,
  created: EMPTY_RANGE
};

function BeneficiariesFilterForm({
  onFilter,
  countries,
  genders,
  facilities,
  initialValues
}) {
  const form = useForm({
    mode: 'onChange',
    // initialValues: filters from the last visit to this list (see index.js)
    defaultValues: { ...DEFAULT_VALUES, ...(initialValues || {}) }
  });

  // Watch all form values and filter automatically on change. Arrays get a
  // new identity on every render, so depend on their serialised form.
  const watchedValues = form.watch();
  const watchedKey = JSON.stringify(watchedValues);

  useEffect(() => {
    onFilter(watchedValues);
  }, [watchedKey]);

  const { sponsorshipPriorities } = useSponsorshipPriorities();
  const { sponsorshipStatuses } = useSponsorshipStatuses();
  const { sponsorshipTypes } = useSponsorshipTypes();

  const countryOptions = useMemo(
    () => countries?.map(country => ({ label: country, value: country })) || [],
    [countries]
  );

  const genderOptions = useMemo(
    () => genders?.map(gender => ({ label: gender, value: gender })) || [],
    [genders]
  );

  const priorityOptions = useMemo(
    () =>
      sponsorshipPriorities?.map(p => ({
        label: p.priority_type,
        value: p.priority_type
      })) || [],
    [sponsorshipPriorities]
  );

  const typeOptions = useMemo(
    () =>
      sponsorshipTypes?.map(t => ({
        label: t.type,
        value: t.type
      })) || [],
    [sponsorshipTypes]
  );

  const statusOptions = useMemo(
    () =>
      [...(sponsorshipStatuses || [])]
        .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100))
        .map(s => ({
          // Hidden (grey) statuses are excluded from the list by default;
          // picking one here is how staff view those children
          label: s.is_hidden ? `${s.status} (hidden)` : s.status,
          value: s.status
        })),
    [sponsorshipStatuses]
  );

  const juzOptions = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        label: `Juz ${i + 1}`,
        value: String(i + 1)
      })),
    []
  );

  const facilityOptions = useMemo(
    () => (facilities || []).map(f => ({ label: f, value: f })),
    [facilities]
  );

  const hasActiveFilters = Object.values(watchedValues).some(v => {
    if (Array.isArray(v)) return v.length > 0;
    // Date ranges are { from, to }
    if (v && typeof v === 'object') return Object.values(v).some(Boolean);
    return Boolean(v);
  });

  return (
    <FormProvider methods={form}>
      <Row className="g-3 align-items-end">
        <Col md={4} lg style={FILTER_FIELD_STYLE}>
          <RHFControl
            name="name"
            label="Name"
            placeholder="Search by name..."
            noOptional={true}
          />
        </Col>

        <Col md={4} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="country"
            label="Location"
            options={countryOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={4} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="gender"
            label="Gender"
            options={genderOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={4} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="priority"
            label="Priority"
            options={priorityOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={6} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="type"
            label="Sponsorship Type"
            options={typeOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={6} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="status"
            label="Status"
            options={statusOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={6} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="juz"
            label="Juz (Hafiz)"
            options={juzOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={4} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="age"
            label="Age"
            options={AGE_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={6} lg style={FILTER_FIELD_STYLE}>
          <RHFSelect
            name="facility"
            label="School / Facility"
            options={facilityOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </Col>

        <Col md={4} lg style={RANGE_COL_STYLE}>
          <RHFDateRangePicker
            name="enrolled"
            label="Enrolment date"
            noOptional={true}
          />
        </Col>

        <Col md={4} lg style={RANGE_COL_STYLE}>
          <RHFDateRangePicker
            name="left"
            label="Left programme"
            noOptional={true}
          />
        </Col>

        <Col md={4} lg style={RANGE_COL_STYLE}>
          <RHFDateRangePicker
            name="created"
            label="Date created"
            noOptional={true}
          />
        </Col>

        {hasActiveFilters && (
          <Col md={4} lg style={FILTER_FIELD_STYLE}>
            <Button
              variant="outline-secondary"
              className="w-100"
              style={{ height: '38px' }}
              onClick={() => form.reset(DEFAULT_VALUES)}
            >
              Clear Filters
            </Button>
          </Col>
        )}
      </Row>
    </FormProvider>
  );
}

BeneficiariesFilterForm.propTypes = {
  onFilter: PropTypes.func.isRequired,
  countries: PropTypes.array,
  genders: PropTypes.array,
  facilities: PropTypes.array,
  initialValues: PropTypes.object
};

export default BeneficiariesFilterForm;
